import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Check, ShoppingCart } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "ASSTj5A6vfhyeolcJJaZq-Um79owztjiz3G40wlByGr1Hj84RNyqm6UT6TyFH-uGPaPfJliy9OwJbnF8";

function PayPalCheckoutButton({ itemType, itemId, amount, onSuccess }) {
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonsRendered, setButtonsRendered] = useState(false);

  useEffect(() => {
    if (window.paypal) {
      setSdkReady(true);
      return;
    }
    const existing = document.querySelector('#paypal-sdk');
    if (existing) {
      const checkPaypal = setInterval(() => {
        if (window.paypal) { setSdkReady(true); clearInterval(checkPaypal); }
      }, 200);
      return () => clearInterval(checkPaypal);
    }
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !amount) return;
    const container = document.getElementById('checkout-paypal-btn');
    if (!container) return;
    container.innerHTML = '';

    window.paypal.Buttons({
      createOrder: async () => {
        setLoading(true);
        try {
          const res = await axios.post(`${API}/checkout/paypal/create`, {
            item_type: itemType,
            item_id: itemId,
          });
          return res.data.order_id;
        } catch (err) {
          setLoading(false);
          throw err;
        }
      },
      onApprove: async (data) => {
        try {
          const res = await axios.post(`${API}/checkout/paypal/capture/${data.orderID}`);
          if (res.data.payment_status === 'paid') onSuccess();
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => setLoading(false),
      onError: () => setLoading(false),
      style: { layout: 'horizontal', color: 'gold', shape: 'rect', label: 'pay', height: 50 },
    }).render('#checkout-paypal-btn').then(() => setButtonsRendered(true));
  }, [sdkReady, amount, itemType, itemId, onSuccess]);

  return (
    <div data-testid="checkout-paypal">
      {(loading || !buttonsRendered) && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">{loading ? 'Processing...' : 'Loading PayPal...'}</span>
        </div>
      )}
      <div id="checkout-paypal-btn" style={{ minHeight: buttonsRendered ? 'auto' : '50px' }} className={loading ? 'opacity-50 pointer-events-none' : ''} />
    </div>
  );
}

export function CheckoutModal({ isOpen, onClose, item, itemType }) {
  const [success, setSuccess] = useState(false);

  if (!item) return null;

  const price = parseFloat(item.checkout_price);

  const handlePaypalSuccess = () => setSuccess(true);

  const handleClose = () => {
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full p-0 bg-card border-border overflow-hidden" data-testid="checkout-modal">
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-serif font-bold text-white mb-2" data-testid="checkout-success">Purchase Complete!</h3>
              <p className="text-muted-foreground mb-4">Thank you for your purchase of <strong>{item.title}</strong>.</p>
              <button onClick={handleClose} className="btn-primary">Done</button>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-serif font-bold text-white" data-testid="checkout-item-title">{item.title}</h3>
                  <p className="text-sm text-muted-foreground capitalize mt-1">{itemType.replace('_', ' ')}</p>
                </div>
                <button onClick={handleClose} className="p-1 rounded hover:bg-muted transition-colors">
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Price Display */}
              <div className="text-center py-4 mb-6 rounded-xl bg-muted/50">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold text-cta" data-testid="checkout-price">
                  ${price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>

              {/* PayPal Payment */}
              <div className="space-y-4">
                <PayPalCheckoutButton
                  itemType={itemType}
                  itemId={item.id}
                  amount={price}
                  onSuccess={handlePaypalSuccess}
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PurchaseButton({ item, itemType, className = '' }) {
  const [showCheckout, setShowCheckout] = useState(false);

  const checkoutPrice = parseFloat(item.checkout_price);
  const hasPrice = checkoutPrice > 0;
  const isFree = item.price?.toLowerCase() === 'free';

  if (isFree) {
    return (
      <a
        href="/contact"
        className={`btn-primary inline-flex items-center gap-2 ${className}`}
        data-testid={`register-btn-${item.id}`}
      >
        Register Free
      </a>
    );
  }

  if (!hasPrice) {
    return (
      <a
        href="/contact"
        className={`btn-primary inline-flex items-center gap-2 ${className}`}
        data-testid={`contact-btn-${item.id}`}
      >
        Contact for Pricing
      </a>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowCheckout(true)}
        className={`btn-primary inline-flex items-center gap-2 ${className}`}
        data-testid={`purchase-btn-${item.id}`}
      >
        <ShoppingCart className="w-4 h-4" />
        Purchase - ${checkoutPrice.toLocaleString()}
      </button>
      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        item={item}
        itemType={itemType}
      />
    </>
  );
}
